import { useRef, useMemo } from 'react';
import { useFrame, extend } from '@react-three/fiber';
import * as THREE from 'three';
import { ParticleShaderMaterial } from '../shaders/particleShader';

extend({ ParticleShaderMaterial });

const PARTICLE_COUNT = 400;
const PARTICLE_SIZE_SCALE = 3;
const _mouseWorld = new THREE.Vector3();
const _dir = new THREE.Vector3();

export default function OrbitalParticles() {
  const pointsRef = useRef();
  const materialRef = useRef();

  const { geometry } = useMemo(() => {
    const geo = new THREE.BufferGeometry();

    const orbitSpeeds = new Float32Array(PARTICLE_COUNT);
    const orbitPhases = new Float32Array(PARTICLE_COUNT);
    const orbitAxes = new Float32Array(PARTICLE_COUNT * 3);
    const orbitRadii = new Float32Array(PARTICLE_COUNT);
    const sizes = new Float32Array(PARTICLE_COUNT);
    // Dummy positions — the vertex shader computes actual positions
    const positions = new Float32Array(PARTICLE_COUNT * 3);

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      // Random orbit axis (normalized random direction)
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      orbitAxes[i * 3] = Math.sin(phi) * Math.cos(theta);
      orbitAxes[i * 3 + 1] = Math.sin(phi) * Math.sin(theta);
      orbitAxes[i * 3 + 2] = Math.cos(phi);

      // Orbit radius — clustered near the sphere with some spread
      // Most particles between 1.8 and 3.5, some outliers up to 5
      const r = Math.random();
      orbitRadii[i] = 1.8 + r * r * 3.2; // quadratic distribution — more near sphere

      // Speed — slower for further orbits (Kepler-ish)
      orbitSpeeds[i] = (0.05 + Math.random() * 0.15) / (orbitRadii[i] * 0.4);

      // Random starting phase
      orbitPhases[i] = Math.random() * Math.PI * 2;

      // Size — smaller particles further out
      sizes[i] =
        (0.02 + Math.random() * 0.06) *
        (1.0 / (orbitRadii[i] * 0.3)) *
        PARTICLE_SIZE_SCALE;

      // Dummy position
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = 0;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('aOrbitSpeed', new THREE.BufferAttribute(orbitSpeeds, 1));
    geo.setAttribute('aOrbitPhase', new THREE.BufferAttribute(orbitPhases, 1));
    geo.setAttribute('aOrbitAxis', new THREE.BufferAttribute(orbitAxes, 3));
    geo.setAttribute('aOrbitRadius', new THREE.BufferAttribute(orbitRadii, 1));
    geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));

    return { geometry: geo };
  }, []);

  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.uTime = state.clock.elapsedTime;

      // Same mouse projection as EyeSphere
      _mouseWorld.set(state.pointer.x, state.pointer.y, 0.5);
      _mouseWorld.unproject(state.camera);
      _dir.copy(_mouseWorld).sub(state.camera.position).normalize();
      const dist = (2.0 - state.camera.position.z) / _dir.z;
      _mouseWorld.copy(state.camera.position).addScaledVector(_dir, dist);

      materialRef.current.uMouseLight.copy(_mouseWorld);
    }
  });

  return (
    <points ref={pointsRef} geometry={geometry}>
      <particleShaderMaterial
        ref={materialRef}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
